import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { forgetGoogleSignOut, googleSignedOut, loadGoogleIdentity } from "../../lib/googleIdentity";
import styles from "./GoogleSignIn.module.css";

/** Google won't draw its button wider than this. */
const GOOGLE_BUTTON_MAX_WIDTH = 400;

type Props = {
  /** Button wording: "Sign in with Google" or "Sign up with Google". */
  text: "signin_with" | "signup_with";
  onSignedIn(): void;
  /** The Google account's address needs the email-code step first (not Gmail/Workspace):
   * the code is already on its way; show the code step for this address. */
  onEmailUnverified(email: string): void;
};

/** Google's own button plus Chrome's automatic prompt, then the "or" line above the email
 * form (ADR 0007). Renders nothing while Google sign-in is off (no client ID configured). */
export const GoogleSignIn = ({ text, onSignedIn, onEmailUnverified }: Props) => {
  const { signInWithGoogle } = useAuth();
  const [clientId, setClientId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const button = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // No config = no Google button; the email form below still works.
    api.authConfig().then(
      (cfg) => setClientId(cfg.google_client_id),
      (e: unknown) => console.warn("Google sign-in config unavailable", e),
    );
  }, []);

  // Set up once per client ID and wording: Google's script keeps the callback it was
  // first given, so re-running for a new onSignedIn identity would only re-prompt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (!clientId) return;
    // After a logout, only the button signs anyone in — no automatic pick, no prompt.
    const quiet = googleSignedOut();
    loadGoogleIdentity().then(
      (gsi) => {
        gsi.initialize({
          client_id: clientId,
          callback: ({ credential }) => {
            setError(null);
            signInWithGoogle(credential).then(
              () => {
                forgetGoogleSignOut();
                onSignedIn();
              },
              (e: unknown) => {
                const email = e instanceof ApiError ? e.body?.email : undefined;
                if (e instanceof ApiError && e.body?.code === "email_unverified" && email) {
                  void api.resendRegistrationCode(email);
                  onEmailUnverified(email);
                  return;
                }
                setError(
                  e instanceof ApiError
                    ? (e.body?.error ?? e.message)
                    : "Couldn't sign in with Google. Try again.",
                );
              },
            );
          },
          auto_select: !quiet,
        });
        // Google can't see our layout from its iframe: tell it the form's width so its
        // button lines up with the "Sign in" button below (issue #6).
        if (button.current) {
          gsi.renderButton(button.current, {
            text,
            size: "large",
            shape: "rectangular",
            width: Math.min(button.current.clientWidth, GOOGLE_BUTTON_MAX_WIDTH),
          });
        }
        if (!quiet) gsi.prompt();
      },
      () => setError("Google sign-in couldn't load. Sign in with your email, or reload the page."),
    );
  }, [clientId, text]);

  if (!clientId) return null;
  return (
    <div className={styles.wrap}>
      <div ref={button} className={styles.button} data-testid="google-button" />
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
      <div className={styles.or}>
        <span>or</span>
      </div>
    </div>
  );
};
